/**
 * Microsoft Clarity Utility
 * Handles initialization for Microsoft Clarity analytics
 * Note: Only runs in production/live environment
 */

import { config } from '../config/env'
import { logger } from '../utils/logger'

/**
 * Initialize Microsoft Clarity
 * Loads the Clarity script with the project ID from environment
 * Only initializes if NOT in test mode (i.e., production/live only)
 */
export const initializeClarity = () => {
  const clarityId = config.clarityProjectId
  
  // Skip if no Clarity ID is provided
  if (!clarityId) {
    logger.debug('ℹ️ Microsoft Clarity not initialized: No project ID provided')
    return
  }

  // Skip if in test/staging mode (only run in production/live)
  if (config.testMode) {
    logger.debug('ℹ️ Microsoft Clarity skipped: Test/Staging mode detected')
    return
  }

  // Check if Clarity is already loaded
  if (window.clarity) {
    logger.debug('ℹ️ Microsoft Clarity already initialized')
    return
  }

  logger.debug('📊 Initializing Microsoft Clarity:', clarityId)

  try {
    // Initialize Clarity using their standard snippet
    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function() {
        (c[a].q = c[a].q || []).push(arguments)
      }
      t = l.createElement(r)
      t.async = 1
      t.src = "https://www.clarity.ms/tag/" + i
      y = l.getElementsByTagName(r)[0]
      y.parentNode.insertBefore(t, y)
    })(window, document, "clarity", "script", clarityId)

    logger.debug('✅ Microsoft Clarity initialized successfully')
  } catch (error) {
    logger.error('❌ Error initializing Microsoft Clarity:', error)
  }
}

/**
 * Set a custom tag in Clarity
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setClarityTag = (key, value) => {
  if (!key || value === undefined || value === null) {
    logger.warn('⚠️ Cannot set Clarity tag: key or value is empty', { key, value })
    return
  }
  
  // Ensure value is a string (Clarity requires string values)
  const stringValue = String(value)
  
  if (window.clarity && typeof window.clarity === 'function') {
    try {
      // Debug: Check if Clarity is properly initialized
      logger.debug('🔍 Clarity function exists, setting tag:', key, '=', stringValue)
      
      // Call Clarity set method
      window.clarity('set', key, stringValue)
      
      logger.debug('✅ Clarity Tag Set:', key, '=', stringValue)
    } catch (error) {
      logger.error('❌ Error setting Clarity tag:', { key, value: stringValue, error: error.message })
    }
  } else {
    logger.warn('⚠️ Microsoft Clarity not initialized or not a function. Tag not set:', key, 'window.clarity:', typeof window.clarity)
  }
}

/**
 * Identify a user in Clarity
 * @param {string} userId - User identifier
 * @param {string} sessionId - Session identifier (optional)
 * @param {string} pageId - Page identifier (optional)
 */
export const identifyClarityUser = (userId, sessionId = null, pageId = null) => {
  if (window.clarity && typeof window.clarity === 'function') {
    try {
      logger.debug('🔍 Clarity function exists, identifying user:', userId, sessionId ? `(Session: ${sessionId})` : '')
      
      window.clarity('identify', userId, sessionId, pageId)
      
      logger.debug('✅ Clarity User Identified:', userId, sessionId ? `(Session: ${sessionId})` : '')
    } catch (error) {
      logger.error('❌ Error identifying Clarity user:', { userId, sessionId, error: error.message })
    }
  } else {
    logger.warn('⚠️ Microsoft Clarity not initialized or not a function. User not identified. window.clarity:', typeof window.clarity)
  }
}

/**
 * Upgrade the current session in Clarity
 * Used to mark important sessions for review
 * @param {string} reason - Reason for upgrading the session
 */
export const upgradeClaritySession = (reason = 'important') => {
  if (window.clarity) {
    try {
      window.clarity('upgrade', reason)
      logger.debug('📊 Clarity Session Upgraded:', reason)
    } catch (error) {
      logger.error('❌ Error upgrading Clarity session:', error)
    }
  } else {
    logger.warn('⚠️ Microsoft Clarity not initialized. Session not upgraded.')
  }
}

export default {
  initializeClarity,
  setClarityTag,
  identifyClarityUser,
  upgradeClaritySession
}
